import * as React from "react";
import * as ReactDOM from "react-dom";

import { groopTheme } from "../styles/MaterialGroopTheme";
import { Theme, ThemeProvider } from "@material-ui/core/styles";
import { GroupContextProvider } from "groopit-js";
import { FilterProvider } from "./State/FilterProvider";
import { GroopitFilterDisplayDTO } from "groopit-js/client";
import { useMediaQuery } from "@material-ui/core";
import { FullScreenModal } from "../Controls/FullScreenModal";
import { useCallback, useState } from "react";
import { InsightsPanel, InsightsPanelTitle } from "../Insights/InsightsPanel";
import { FilterPanel, FilterPanelTitle } from "./FilterPanel";
import { InsightsPanelProvider } from "../Insights/InsightsPanelContext";
import "./SearchFeed.css";
import { FeedProvider } from "./State/FeedContext";
import { FeedPanel } from "./FeedPanel";

interface Props {
  owner: boolean;
  pictureImageBase: string;
  groopId: string;
  user_id: string;
  filter?: GroopitFilterDisplayDTO;
  insightsReportId?: string;
  insightsReportEdit: boolean;
  suggestedAssignmentIds: string[];
  enable_chat: boolean;
}

export enum FeedModalTypes {
  "Filters",
  "Insights",
}

interface FeedModalStackNavigator {
  pushModalStack?: (modal: FeedModalTypes) => void;
  popModalStack?: () => void;
}

export const FeedModalStackContext =
  React.createContext<FeedModalStackNavigator>({});

export const SearchFeed = (props: Props): JSX.Element => {
  const isSingleColumn = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg"),
  );

  const [modalStack, setModalStack] = useState<FeedModalTypes[]>([]);

  const pushModalStack = useCallback(
    (modal: FeedModalTypes) => {
      modalStack.push(modal);
      setModalStack([...modalStack]);
    },
    [modalStack],
  );

  const popModalStack = useCallback(() => {
    modalStack.pop();
    setModalStack([...modalStack]);
  }, [modalStack]);

  return (
    <GroupContextProvider {...props}>
      <FeedModalStackContext.Provider value={{ pushModalStack, popModalStack }}>
        <FilterProvider {...props} lastUsedFilterPrefix="SearchFeed">
          <FeedProvider {...props}>
            <InsightsPanelProvider {...props}>
              <div className="searchfeed gap-6">
                <div
                  id="feed-container" // ID required for the tour to work
                  className="grouppage-content feed"
                >
                  <FeedPanel {...props} />
                </div>
                <div className="filter">
                  <FilterPanel variant="column" />
                </div>
                <div className="insights">
                  <InsightsPanel variant="column" />
                </div>
              </div>
              {isSingleColumn && (
                <>
                  <FullScreenModal
                    open={
                      modalStack.length > 0 &&
                      modalStack[modalStack.length - 1] ===
                        FeedModalTypes.Filters
                    }
                    onClose={popModalStack}
                    title={FilterPanelTitle}
                  >
                    <FilterPanel variant="modal" onClose={popModalStack} />
                  </FullScreenModal>
                  <FullScreenModal
                    open={
                      modalStack.length > 0 &&
                      modalStack[modalStack.length - 1] ===
                        FeedModalTypes.Insights
                    }
                    onClose={() => setModalStack(modalStack.slice(0, -1))}
                    title={InsightsPanelTitle}
                  >
                    <InsightsPanel variant="modal" />
                  </FullScreenModal>
                </>
              )}
            </InsightsPanelProvider>
          </FeedProvider>
        </FilterProvider>
      </FeedModalStackContext.Provider>
    </GroupContextProvider>
  );
};

const searchresults = document.querySelector(".searchresults") as HTMLElement;
if (searchresults) {
  ReactDOM.render(
    <ThemeProvider theme={groopTheme}>
      <SearchFeed
        owner={searchresults.dataset.isowner === "True"}
        user_id={searchresults.dataset.userid as string}
        pictureImageBase={searchresults.dataset.pictureimagebase as string}
        groopId={searchresults.dataset.groopid as string}
        filter={GroopitFilterDisplayDTO.fromJS(
          JSON.parse(searchresults.dataset.filter as string),
        )}
        insightsReportId={JSON.parse(
          searchresults.dataset.insightsReportId as string,
        )}
        insightsReportEdit={searchresults.dataset.insightsReportEdit === "True"}
        suggestedAssignmentIds={JSON.parse(
          searchresults.dataset.suggestedAssignmentIds ?? "[]",
        )}
        enable_chat={searchresults.dataset.enableChat === "True"}
      />
    </ThemeProvider>,
    searchresults,
  );
}
